import { useEffect, useRef, useCallback } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';

export interface UseExcelNavigationProps {
  data: any[];
  columns: any[];
  onCellSelect?: (rowIndex: number, colIndex: number, cell: HTMLElement) => void;
  onCellEdit?: (rowIndex: number, colIndex: number, value: any) => void;
  enableEditing?: boolean;
}

export const useExcelNavigation = ({
  data,
  columns,
  onCellSelect,
  onCellEdit,
  enableEditing = true
}: UseExcelNavigationProps) => {
  const tableRef = useRef<HTMLTableElement>(null);
  const currentCell = useRef<{ row: number; col: number }>({ row: 0, col: 0 });
  const isEditing = useRef(false);

  // Get all focusable cells in the table
  const getFocusableCells = useCallback(() => {
    if (!tableRef.current) return [];
    const rows = tableRef.current.querySelectorAll('tbody tr');
    const cells: HTMLElement[][] = [];
    
    rows.forEach((row, rowIndex) => {
      const rowCells = Array.from(row.querySelectorAll('td'));
      cells[rowIndex] = rowCells.map(cell => 
        cell.querySelector('[data-excel-cell]') as HTMLElement || cell
      );
    });
    
    return cells;
  }, []);

  // Focus a specific cell
  const focusCell = useCallback((row: number, col: number) => {
    const cells = getFocusableCells();
    if (cells[row]?.[col]) {
      cells[row][col].focus();
      onCellSelect?.(row, col, cells[row][col]);
      currentCell.current = { row, col };
    }
  }, [getFocusableCells, onCellSelect]);

  // Navigate between cells
  const navigateCell = useCallback((direction: 'up' | 'down' | 'left' | 'right') => {
    if (isEditing.current) return;
    
    const cells = getFocusableCells();
    const maxRow = cells.length - 1;
    const maxCol = cells[0]?.length ? cells[0].length - 1 : 0;

    let newRow = currentCell.current.row;
    let newCol = currentCell.current.col;

    switch (direction) {
      case 'up':
        newRow = Math.max(0, newRow - 1);
        break;
      case 'down':
        newRow = Math.min(maxRow, newRow + 1);
        break;
      case 'left':
        newCol = Math.max(0, newCol - 1);
        break;
      case 'right':
        newCol = Math.min(maxCol, newCol + 1);
        break;
    }

    focusCell(newRow, newCol);
  }, [focusCell, getFocusableCells]);

  // Start editing current cell
  const startEditing = useCallback(() => {
    if (!enableEditing) return;
    
    const cells = getFocusableCells();
    const current = cells[currentCell.current.row]?.[currentCell.current.col];
    if (current && current.getAttribute('data-editable') === 'true') {
      isEditing.current = true;
      current.setAttribute('contenteditable', 'true');
      current.focus();
      
      // Select all text when editing starts
      const selection = window.getSelection();
      const range = document.createRange();
      range.selectNodeContents(current);
      selection?.removeAllRanges();
      selection?.addRange(range);
    }
  }, [enableEditing, getFocusableCells]);

  // Stop editing and save value
  const stopEditing = useCallback(() => {
    const cells = getFocusableCells();
    const current = cells[currentCell.current.row]?.[currentCell.current.col];
    if (current && current.hasAttribute('contenteditable')) {
      isEditing.current = false;
      current.removeAttribute('contenteditable');
      const value = current.textContent || '';
      onCellEdit?.(currentCell.current.row, currentCell.current.col, value);
    }
  }, [getFocusableCells, onCellEdit]);

  // Keyboard shortcuts
  useHotkeys('up', (e) => {
    e.preventDefault();
    if (isEditing.current) return;
    navigateCell('up');
  }, { enableOnFormTags: true });

  useHotkeys('down', (e) => {
    e.preventDefault();
    if (isEditing.current) return;
    navigateCell('down');
  }, { enableOnFormTags: true });

  useHotkeys('left', (e) => {
    e.preventDefault();
    if (isEditing.current) return;
    navigateCell('left');
  }, { enableOnFormTags: true });

  useHotkeys('right', (e) => {
    e.preventDefault();
    if (isEditing.current) return;
    navigateCell('right');
  }, { enableOnFormTags: true });

  useHotkeys('tab', (e) => {
    e.preventDefault();
    if (isEditing.current) {
      stopEditing();
    }
    navigateCell('right');
  }, { enableOnFormTags: true });

  useHotkeys('shift+tab', (e) => {
    e.preventDefault();
    if (isEditing.current) {
      stopEditing();
    }
    navigateCell('left');
  }, { enableOnFormTags: true });

  useHotkeys('enter', (e) => {
    e.preventDefault();
    if (isEditing.current) {
      stopEditing();
      // Move to next row after editing
      setTimeout(() => navigateCell('down'), 10);
    } else {
      startEditing();
    }
  }, { enableOnFormTags: true });

  useHotkeys('escape', (e) => {
    e.preventDefault();
    if (isEditing.current) {
      stopEditing();
    }
  }, { enableOnFormTags: true });

  // Select all text when cell is focused
  useEffect(() => {
    const handleFocus = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      if (target.getAttribute('data-excel-cell') && !isEditing.current) {
        // Find which cell was focused
        const cells = getFocusableCells();
        cells.forEach((row, rowIndex) => {
          const colIndex = row.indexOf(target);
          if (colIndex !== -1) {
            currentCell.current = { row: rowIndex, col: colIndex };
            onCellSelect?.(rowIndex, colIndex, target);
          }
        });
      }
    };

    document.addEventListener('focusin', handleFocus);
    return () => document.removeEventListener('focusin', handleFocus);
  }, [getFocusableCells, onCellSelect]);

  return {
    tableRef,
    currentCell: currentCell.current,
    focusCell,
    navigateCell,
    startEditing,
    stopEditing,
    isEditing: isEditing.current
  };
};